import { useState } from 'react'
import { Card, Empty } from 'antd'
import { useAuthStore } from '../store/authStore'
import BroadcastSettings from '../components/BroadcastSettings'
import QuickTemplatesSettings from '../components/QuickTemplatesSettings'
import CrmIntegrationSettings from '../components/CrmIntegrationSettings'
import PrivacySettings from '../components/PrivacySettings'
import FeatureSettings from '../components/FeatureSettings'
import SlaRulesSettings from '../components/SlaRulesSettings'
import AutoAssignSettings from '../components/AutoAssignSettings'
import RolesSettings from '../components/RolesSettings'
import StatusAccessSettings from '../components/StatusAccessSettings'
import wisewayLogo from '../assets/wiseway-crm.png'
import { canManageRoles, managesSettings } from '../constants/roles'

// Подразделы настроек. Показывается один за раз: разделы разрослись, и листать
// их одной длинной страницей стало неудобно.
type SectionKey = 'broadcast' | 'crm' | 'sla' | 'autoassign' | 'privacy' | 'features' | 'roles' | 'statuses'

// rolesAdmin - подразделы только для «Админа разработки»: это настройка того,
// кто что может в системе.
const SECTIONS: { key: SectionKey; label: string; rolesAdmin?: boolean }[] = [
    { key: 'broadcast', label: 'Рассылка первых сообщений' },
    { key: 'crm', label: 'Интеграция с CRM Wiseway' },
    { key: 'sla', label: 'SLA' },
    { key: 'autoassign', label: 'Автораспределение' },
    { key: 'privacy', label: 'Доступ операторов' },
    { key: 'features', label: 'Доп. функции' },
    { key: 'roles', label: 'Роли и права', rolesAdmin: true },
    { key: 'statuses', label: 'Доступ к статусам', rolesAdmin: true },
]

const cardStyle = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    marginBottom: 24,
}

// Общие настройки системы. Всё, что касается учётной записи оператора
// (логин, пароль, аватар), живёт отдельно на странице «Учётная запись»,
// которая открывается из меню профиля.
export default function Settings() {
    const { user } = useAuthStore()
    const isAdmin = managesSettings(user?.role)
    const isRoleManager = canManageRoles(user?.role)
    const [section, setSection] = useState<SectionKey>('broadcast')

    // Оформление повторяет пункты верхнего меню приложения.
    const sectionPill = (active: boolean) => ({
        padding: '6px 12px',
        borderRadius: 5,
        fontSize: 14,
        cursor: 'pointer',
        whiteSpace: 'nowrap' as const,
        background: active ? 'oklch(0.28 0.015 260)' : 'transparent',
        color: active ? 'oklch(0.95 0.01 260)' : 'oklch(0.65 0.01 260)',
        fontWeight: active ? 600 : 500,
    })

    return (
        <div style={{
            padding: 24,
            // Таблицы ролей и статусов широкие - под них страница раздвигается,
            // чтобы все роли помещались без горизонтальной прокрутки.
            maxWidth: section === 'roles' || section === 'statuses' ? 1640 : 1100,
            margin: '0 auto',
        }}>
            {/* Заголовок «Настройки» дублировал пункт верхнего меню — остались
                только переключатели подразделов. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', marginBottom: 24 }}>
                {isAdmin && (
                    <div style={{
                        display: 'flex',
                        // Подразделов стало восемь: в узком окне они переносятся на
                        // вторую строку, а не уводят страницу в горизонтальную прокрутку.
                        flexWrap: 'wrap',
                        background: 'var(--bg-base)',
                        borderRadius: 7,
                        padding: 2,
                        gap: 2,
                    }}>
                        {SECTIONS.filter((item) => !item.rolesAdmin || isRoleManager).map((item) => (
                            <div
                                key={item.key}
                                style={sectionPill(section === item.key)}
                                onClick={() => setSection(item.key)}
                            >
                                {item.label}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {!isAdmin && <Empty description="Здесь пока нет доступных вам настроек" />}

            {isAdmin && section === 'broadcast' && (
                <>
                    <Card title="Шаблоны и правила рассылки" style={cardStyle}>
                        <BroadcastSettings />
                    </Card>

                    <Card title="Быстрые шаблоны сообщений" style={cardStyle}>
                        <QuickTemplatesSettings />
                    </Card>
                </>
            )}

            {isAdmin && section === 'sla' && (
                <Card title="SLA по статусам и автозадачи" style={cardStyle}>
                    <SlaRulesSettings />
                </Card>
            )}

            {isAdmin && section === 'autoassign' && (
                <Card title="Автораспределение ответивших лидов" style={cardStyle}>
                    <AutoAssignSettings />
                </Card>
            )}

            {isRoleManager && section === 'roles' && (
                <Card title="Роли и права" style={cardStyle}>
                    <RolesSettings />
                </Card>
            )}

            {isRoleManager && section === 'statuses' && (
                <Card title="Доступ к статусам" style={cardStyle}>
                    <StatusAccessSettings />
                </Card>
            )}

            {isAdmin && section === 'features' && (
                <Card title="Дополнительные функции" style={cardStyle}>
                    <FeatureSettings />
                </Card>
            )}

            {isAdmin && section === 'privacy' && (
                <Card title="Что видит оператор" style={cardStyle}>
                    <PrivacySettings />
                </Card>
            )}

            {isAdmin && section === 'crm' && (
                <Card
                    title="Интеграция с CRM Wiseway"
                    style={cardStyle}
                    // Логотип живёт в отдельном слоте шапки карточки: заголовок
                    // остаётся на месте, высота шапки не меняется.
                    extra={(
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '5px 10px',
                            borderRadius: 8,
                            // Подложка под цвет фона самого логотипа: надпись на нём
                            // тёмная и на тёмной теме иначе не читалась бы.
                            background: '#ffffff',
                            lineHeight: 0,
                        }}>
                            <img
                                src={wisewayLogo}
                                alt="Wiseway CRM"
                                style={{ height: 22, width: 'auto', display: 'block' }}
                            />
                        </span>
                    )}
                >
                    <CrmIntegrationSettings />
                </Card>
            )}
        </div>
    )
}
